/**
 * aramVoidAtmosphere — AR-R30 / SPY-202
 * Purple edge haze + soft fog veil for Murder Bridge void readability.
 *
 * Public API:
 *   ARAM_VOID_ATMOSPHERE — frozen color/geometry knobs
 *   buildVoidAtmosphere(THREE) → THREE.Group
 */

import {
  Group,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  RingGeometry
} from 'three';
import { ARAM_LANE_CONFIG } from './aramBridgeMeshes.js';

export const ARAM_VOID_ATMOSPHERE = Object.freeze({
  hazeColor: 0x6b21a8,
  hazeEmissive: 0x7c3aed,
  hazeOpacity: 0.42,
  fogColor: 0x2e1065,
  fogOpacity: 0.28,
  fogY: -0.02,
  hazeY: 0.01,
  /** Outer ring beyond void plane so bridge edges read against purple haze. */
  hazeOuterScale: 1.18,
  hazeInnerScale: 0.92
});

/**
 * @param {object} [THREE]
 * @returns {object} named group with fog veil + edge haze ring
 */
export function buildVoidAtmosphere(THREE) {
  const GroupCls = THREE?.Group || Group;
  const group = new GroupCls();
  group.name = 'aram-void-atmosphere';
  group.add(buildFogVeil(THREE));
  group.add(buildEdgeHaze(THREE));
  return group;
}

function buildFogVeil(THREE) {
  const PlaneGeo = THREE?.PlaneGeometry || PlaneGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const size = ARAM_LANE_CONFIG.voidSize * ARAM_VOID_ATMOSPHERE.hazeOuterScale;
  const mat = new MeshMat({
    color: ARAM_VOID_ATMOSPHERE.fogColor,
    transparent: true,
    opacity: ARAM_VOID_ATMOSPHERE.fogOpacity,
    roughness: 1,
    depthWrite: false
  });
  const mesh = new MeshCls(new PlaneGeo(size, size), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = ARAM_VOID_ATMOSPHERE.fogY;
  mesh.name = 'aram-void-fog';
  mesh.userData.voidAtmosphere = 'fog';
  return mesh;
}

function buildEdgeHaze(THREE) {
  const RingGeo = THREE?.RingGeometry || RingGeometry;
  const MeshMat = THREE?.MeshStandardMaterial || MeshStandardMaterial;
  const MeshCls = THREE?.Mesh || Mesh;
  const half = ARAM_LANE_CONFIG.voidSize / 2;
  const inner = half * ARAM_VOID_ATMOSPHERE.hazeInnerScale;
  const outer = half * ARAM_VOID_ATMOSPHERE.hazeOuterScale;
  const mat = new MeshMat({
    color: ARAM_VOID_ATMOSPHERE.hazeColor,
    emissive: ARAM_VOID_ATMOSPHERE.hazeEmissive,
    emissiveIntensity: 0.55,
    transparent: true,
    opacity: ARAM_VOID_ATMOSPHERE.hazeOpacity,
    roughness: 1,
    depthWrite: false,
    side: 2
  });
  const mesh = new MeshCls(new RingGeo(inner, outer, 48), mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = ARAM_VOID_ATMOSPHERE.hazeY;
  mesh.name = 'aram-void-edge-haze';
  mesh.userData.voidAtmosphere = 'edgeHaze';
  return mesh;
}
